import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "../config";
import { analyzeVideos, countByKey, nameDateTime, pathsByDate, summarize } from "../utils/geo";
import { detectUTurns, headingArrows, trackEnds, videoColor } from "../utils/track";
import { formatDateTime } from "../utils/parsers";
import MonthCalendar, { monthLabel } from "./MonthCalendar";

export const MARKER_COLORS = {
  day: "#2e7d32",
  night: "#283593",
  unknown: "#616161",
  corrupt: "#c62828",
  "no-gps": "#ef6c00",
};

/** One heading arrow per minute of video — five on a five-minute clip. */
export const ARROW_INTERVAL_SECONDS = 60;

/** Seconds from the start of a video as m:ss. */
export function formatOffset(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const STATUS_LABEL = {
  ok: "OK",
  corrupt: "Corrupt / unreadable",
  "no-gps": "Metadata issue: no GPS",
};

const TIME_SOURCE_LABEL = {
  gps: "GPS clock",
  filename: "file name",
  container: "container date",
  "file-modified": "file modified time",
};

export function markerColor(item) {
  return item.status === "ok"
    ? MARKER_COLORS[item.period] || MARKER_COLORS.unknown
    : MARKER_COLORS[item.status];
}

function markerStyle(item, focused) {
  const color = markerColor(item);
  const estimated = item.position.estimated;
  return {
    color,
    fillColor: color,
    fillOpacity: estimated ? 0.25 : 0.85,
    weight: focused ? 4 : estimated ? 2 : 1,
    dashArray: estimated ? "3 3" : undefined,
    radius: item.status === "ok" ? 6 : 9,
  };
}

// Leaflet icons are plain HTML, so these are styled inline rather than from
// the stylesheet — it keeps the marker and its colour in one place, and the
// colour is per video and therefore not expressible as a static class.

/** The "1s" / "1e" badge at each end of a track. */
function endpointIcon(label, color) {
  return L.divIcon({
    className: "",
    html:
      `<span style="display:flex;align-items:center;justify-content:center;` +
      `width:22px;height:22px;border-radius:50%;background:${color};color:#fff;` +
      `font:600 11px/1 system-ui,sans-serif;border:2px solid #fff;` +
      `box-shadow:0 0 0 1px rgba(0,0,0,.35)">${label}</span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

/** A heading arrow. 0° is north, which is where the glyph already points. */
function arrowIcon(bearing, color) {
  return L.divIcon({
    className: "",
    html:
      `<div style="transform:rotate(${bearing}deg);width:16px;height:16px;` +
      `display:flex;align-items:center;justify-content:center;` +
      `color:${color};font-size:15px;line-height:1;` +
      `text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 2px #fff">\u25B2</div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/** Where the vehicle turned back on itself. */
function uTurnIcon(color) {
  return L.divIcon({
    className: "",
    html:
      `<span style="display:flex;align-items:center;justify-content:center;` +
      `width:20px;height:20px;border-radius:50%;background:#fff;color:${color};` +
      `font:700 11px/1 system-ui,sans-serif;border:2px solid ${color}">U</span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

const boundsOf = (map) => {
  const b = map.getBounds();
  return { south: b.getSouth(), north: b.getNorth(), west: b.getWest(), east: b.getEast() };
};

function BoundsWatcher({ onChange }) {
  const map = useMapEvents({
    moveend: () => onChange(boundsOf(map)),
    zoomend: () => onChange(boundsOf(map)),
  });
  useEffect(() => {
    onChange(boundsOf(map));
  }, [map, onChange]);
  return null;
}

function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) map.fitBounds(points, { padding: [30, 30], maxZoom: 15 });
  }, [map, points]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, Math.max(map.getZoom(), 14));
  }, [map, target]);
  return null;
}

export function describeTime(item) {
  if (!item.time) return "No timestamp";
  return `${item.time.toLocaleString()} (from ${TIME_SOURCE_LABEL[item.timeSource] || item.timeSource})`;
}

export function describePosition(item) {
  if (!item.position) return "No location";
  if (!item.position.estimated) return "GPS location";
  const method = item.position.method;
  if (method === "interpolated") {
    return `Estimated location: interpolated between ${item.position.basedOn.join(" and ")}`;
  }
  return `Estimated location: near ${item.position.basedOn[0]} (${method === "after-previous" ? "previous" : "next"} video)`;
}

function MarkerTooltip({ item }) {
  return (
    <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
      <div className="map-tooltip">
        <strong>{item.name}</strong>
        <div>{describeTime(item)}</div>
        <div>
          {item.period === "unknown" ? "Day/night unknown" : item.period === "day" ? "Day" : "Night"}
          {" · "}
          {STATUS_LABEL[item.status]}
        </div>
        <div>{describePosition(item)}</div>
        <div className={item.dbStatus === "missing" ? "map-tooltip-error" : undefined}>{describeDb(item)}</div>
        {item.error && <div className="map-tooltip-error">ExifTool: {item.error}</div>}
        {item.warnings?.length > 0 && (
          <div className="map-tooltip-error">{item.warnings.join("; ")}</div>
        )}
      </div>
    </Tooltip>
  );
}

export const DB_STATUS_LABEL = {
  missing: "NOT in database",
  pending: "In database, waiting for processing",
  processed: "In database, processed",
  error: "In database, with errors",
  unknown: "Database not checked",
};

const DB_MISSING_COLOR = "#ad1457";

export function describeDb(item) {
  const label = DB_STATUS_LABEL[item.dbStatus] || DB_STATUS_LABEL.unknown;
  const db = item.db;
  if (!db || !db.registered) return label;
  const parts = [`${db.active} record(s)`];
  if (db.assetTypes?.length) parts.push(db.assetTypes.join(", "));
  if (db.errors?.length) parts.push(db.errors.join("; "));
  return `${label}: ${parts.join(" · ")}`;
}

// When and how a video was recorded and processed, for the routes list.
// The mode decides which of the two leads.
export function describeDates(item, mode) {
  const recorded = nameDateTime(item.name) || (item.time ? formatDateTime(item.time) : null);
  const processed = item.processedAt ? formatDateTime(item.processedAt) : null;
  const rec = `Recorded ${recorded || "unknown"}`;
  const proc = processed ? `Processed ${processed}` : "Not processed yet";
  return mode === "processed" ? `${proc} · ${rec}` : `${rec} · ${proc}`;
}

export default function MapTab({
  data,
  status,
  error,
  onReload,
  registerMissing,
  month = null,
  mode = "video",
  onMonthChange,
  onModeChange,
}) {
  const items = useMemo(
    () => analyzeVideos(data?.videos, { maxGapMinutes: config.mapMaxGapMinutes, timeZone: data?.timeZone }),
    [data],
  );
  // The day a video belongs to on the calendar: recorded (file name) or processed.
  const keyField = mode === "processed" ? "processedKey" : "dateKey";
  const [selectedDay, setSelectedDay] = useState(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [focus, setFocus] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [registerMessage, setRegisterMessage] = useState(null);

  // A new month or mode starts from the whole month again.
  useEffect(() => {
    setSelectedDay(null);
  }, [month, mode]);

  // Videos of the month in view. Without a month (older callers, tests)
  // everything is shown. Undated files stay visible in recorded mode so a
  // corrupt file is never hidden; in processed mode only processed videos
  // have a day at all.
  const monthItems = useMemo(() => {
    if (!month) return items;
    return items.filter((i) => {
      const key = i[keyField];
      if (!key || key === "unknown") return mode !== "processed";
      return key.startsWith(`${month}-`);
    });
  }, [items, month, mode, keyField]);
  const counts = useMemo(() => countByKey(monthItems, keyField), [monthItems, keyField]);

  const visible = useMemo(() => {
    const byDay = selectedDay ? monthItems.filter((i) => i[keyField] === selectedDay) : monthItems;
    return onlyMissing ? byDay.filter((i) => i.dbStatus === "missing") : byDay;
  }, [monthItems, selectedDay, keyField, onlyMissing]);
  const scopeLabel = selectedDay || (month ? monthLabel(month) : "all dates");
  const missing = useMemo(() => visible.filter((i) => i.dbStatus === "missing"), [visible]);

  const handleRegister = async (names) => {
    if (!registerMissing || names.length === 0) return;
    setRegistering(true);
    setRegisterMessage(null);
    try {
      const response = await registerMissing(names);
      const results = Array.isArray(response?.results) ? response.results : [];
      const ok = results.filter((r) => r.status === "registered" || r.status === "already-registered").length;
      const failed = results.filter((r) => r.status === "failed");
      setRegisterMessage({
        tone: failed.length ? "error" : "success",
        text: failed.length
          ? `${ok} registered, ${failed.length} failed: ${failed.map((f) => `${f.fileName} (${f.error})`).join("; ")}`
          : `${ok} video(s) registered in the database.`,
      });
      onReload(false);
    } catch (err) {
      setRegisterMessage({ tone: "error", text: `Could not register: ${err.message}` });
    } finally {
      setRegistering(false);
    }
  };
  const points = useMemo(
    () => visible.filter((i) => i.position).map((i) => [i.position.lat, i.position.lon]),
    [visible],
  );
  const paths = useMemo(() => pathsByDate(visible), [visible]);

  // One colour, one number and one set of heading arrows per video on screen.
  // Numbered over `visible` so the badges match the legend even when a date
  // filter is applied, and ordered by time because analyzeVideos sorts that way.
  const routes = useMemo(
    () =>
      visible.map((item, i) => {
        const color = videoColor(i);
        const arrows = headingArrows(item, ARROW_INTERVAL_SECONDS);
        return {
          item,
          color,
          number: i + 1,
          arrows,
          uTurns: detectUTurns(arrows),
          ...trackEnds(item),
        };
      }),
    [visible],
  );
  const inView = summarize(visible, bounds);
  const overall = summarize(visible);
  const issues = visible.filter((i) => i.status !== "ok" || i.dbStatus === "missing" || i.dbStatus === "error");
  const onBounds = useCallback((b) => setBounds(b), []);

  const focusItem = (item) => {
    if (item.position) setFocus({ key: item.file, latlng: [item.position.lat, item.position.lon] });
  };

  return (
    <div className="map-tab">
      <div className="map-canvas">
        <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom className="map-leaflet">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <BoundsWatcher onChange={onBounds} />
          <FitToPoints points={points} />
          <FlyTo target={focus?.latlng} />

          {Object.entries(paths).map(([date, path]) =>
            path.length > 1 ? (
              <Polyline
                key={`path-${date}`}
                positions={path}
                pathOptions={{ color: "#9e9e9e", weight: 1.5, dashArray: "4 6", opacity: 0.8 }}
              />
            ) : null,
          )}

          {routes.map(({ item, color }) =>
            item.track && item.track.length > 1 ? (
              <Polyline
                key={`track-${item.file}`}
                positions={item.track}
                pathOptions={{ color, weight: 3, opacity: 0.75 }}
              />
            ) : null,
          )}

          {routes.map(({ item, color, number, arrows, uTurns, start, end }) => (
            <Fragment key={`route-${item.file}`}>
              {arrows.map((a) => (
                <Marker
                  key={`arrow-${item.file}-${a.second}`}
                  position={[a.lat, a.lon]}
                  icon={arrowIcon(a.bearing, color)}
                  interactive={false}
                  zIndexOffset={200}
                />
              ))}
              {uTurns.map((t) => (
                <Marker
                  key={`uturn-${item.file}-${t.second}`}
                  position={[t.lat, t.lon]}
                  icon={uTurnIcon(color)}
                  zIndexOffset={400}
                >
                  <Tooltip direction="top" offset={[0, -8]}>
                    {item.name}: turns back {t.delta}&deg; at {formatOffset(t.second)}
                  </Tooltip>
                </Marker>
              ))}
              {start && (
                <Marker
                  position={[start.lat, start.lon]}
                  icon={endpointIcon(`${number}s`, color)}
                  zIndexOffset={600}
                  eventHandlers={{ click: () => focusItem(item) }}
                >
                  <Tooltip direction="top" offset={[0, -12]}>{item.name} — start</Tooltip>
                </Marker>
              )}
              {end && (
                <Marker
                  position={[end.lat, end.lon]}
                  icon={endpointIcon(`${number}e`, color)}
                  zIndexOffset={600}
                  eventHandlers={{ click: () => focusItem(item) }}
                >
                  <Tooltip direction="top" offset={[0, -12]}>{item.name} — end</Tooltip>
                </Marker>
              )}
            </Fragment>
          ))}

          {visible.map((item) =>
            item.position && item.dbStatus === "missing" ? (
              <CircleMarker
                key={`db-${item.file}`}
                center={[item.position.lat, item.position.lon]}
                pathOptions={{ color: DB_MISSING_COLOR, weight: 3, dashArray: "2 4", fill: false }}
                radius={markerStyle(item).radius + 5}
                interactive={false}
              />
            ) : null,
          )}

          {visible.map((item) =>
            item.position ? (
              <CircleMarker
                key={item.file}
                center={[item.position.lat, item.position.lon]}
                pathOptions={markerStyle(item, focus?.key === item.file)}
                radius={markerStyle(item).radius}
                eventHandlers={{ click: () => focusItem(item) }}
              >
                <MarkerTooltip item={item} />
              </CircleMarker>
            ) : null,
          )}
        </MapContainer>
      </div>

      <aside className="map-panel">
        <div className="map-panel-header">
          <h3>Map View</h3>
          <div className="map-actions">
            <button onClick={() => onReload(false)} disabled={status === "loading"}>
              Reload
            </button>
            <button onClick={() => onReload(true)} disabled={status === "loading"} title="Re-extract metadata for every file">
              Rescan files
            </button>
          </div>
        </div>

        {status === "loading" && <p className="status-text">Reading video metadata...</p>}
        {status === "error" && (
          <p className="status-text error">
            Could not load video metadata: {error}. Is the metadata service running at {config.metadataBaseUrl}?
          </p>
        )}
        {data && (
          <p className="map-meta">
            {data.count} file(s) in {data.videoDir}
            {data.extractedNow > 0 ? `, ${data.extractedNow} extracted just now` : ""}
            {data.scan?.running ? `, reading ${data.scan.done} / ${data.scan.total}` : ""}
            {data.pendingCount > 0 && !data.scan?.running ? `, ${data.pendingCount} not read yet` : ""}
            {data.timeZone ? `, times read as ${data.timeZone}` : ""}
          </p>
        )}
        {data?.dbError && (
          <p className="status-text error">Database check failed: {data.dbError}</p>
        )}

        {month && (
          <MonthCalendar
            month={month}
            counts={counts}
            selected={selectedDay}
            onSelect={setSelectedDay}
            onMonthChange={onMonthChange}
            mode={mode}
            onModeChange={onModeChange}
          />
        )}

        <label className="map-field map-check">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          Only videos not in the database
        </label>

        <div className="map-count-block map-db-block">
          <div className="map-count-title">Database check ({scopeLabel})</div>
          <div className="map-count-row"><span className="dot db-missing" />Not in database<b data-testid="count-db-missing">{overall.dbMissing}</b></div>
          <div className="map-count-row"><span className="dot day" />Processed<b data-testid="count-db-processed">{overall.dbProcessed}</b></div>
          <div className="map-count-row"><span className="dot unknown" />Waiting for processing<b data-testid="count-db-pending">{overall.dbPending}</b></div>
          <div className="map-count-row"><span className="dot corrupt" />With errors<b data-testid="count-db-error">{overall.dbError}</b></div>
          {overall.dbUnknown > 0 && (
            <div className="map-count-row"><span className="dot unknown" />Not checked<b>{overall.dbUnknown}</b></div>
          )}
          {missing.length > 0 && registerMissing && (
            <button
              className="map-register-all"
              onClick={() => handleRegister(missing.map((i) => i.name))}
              disabled={registering}
            >
              {registering ? "Registering..." : `Register ${missing.length} missing video(s) in database`}
            </button>
          )}
          {registerMessage && (
            <p className={`status-text ${registerMessage.tone === "error" ? "error" : ""}`}>{registerMessage.text}</p>
          )}
        </div>

        <div className="map-counts">
          <div className="map-count-block">
            <div className="map-count-title">In current map view</div>
            <div className="map-count-row"><span className="dot day" />Day videos<b data-testid="count-day-view">{inView.day}</b></div>
            <div className="map-count-row"><span className="dot night" />Night videos<b data-testid="count-night-view">{inView.night}</b></div>
            <div className="map-count-row"><span className="dot estimated" />Estimated positions<b>{inView.estimated}</b></div>
          </div>
          <div className="map-count-block">
            <div className="map-count-title">{selectedDay ? selectedDay : month ? `All of ${monthLabel(month)}` : "All dates"}</div>
            <div className="map-count-row"><span className="dot day" />Day videos<b data-testid="count-day-total">{overall.day}</b></div>
            <div className="map-count-row"><span className="dot night" />Night videos<b data-testid="count-night-total">{overall.night}</b></div>
            <div className="map-count-row"><span className="dot corrupt" />Corrupt / unreadable<b data-testid="count-corrupt">{overall.corrupt}</b></div>
            <div className="map-count-row"><span className="dot no-gps" />Missing GPS<b data-testid="count-no-gps">{overall.noGps}</b></div>
            <div className="map-count-row"><span className="dot unknown" />Not on map<b>{overall.unplottable}</b></div>
          </div>
        </div>

        <div className="map-legend">
          <span className="map-legend-note">Numbered badges and lines: one colour per video (1s = start, 1e = end), nothing else.</span>
          <span className="map-legend-note">Status of a video (dot in the lists, point on the map):</span>
          <span><i className="dot day" />Day</span>
          <span><i className="dot night" />Night</span>
          <span><i className="dot corrupt" />Corrupt</span>
          <span><i className="dot no-gps" />No GPS (not on the map)</span>
          <span><i className="dot db-missing" />Dashed ring = not in database</span>
          <span><i className="dot estimated" />Dashed = estimated position</span>
          <span>&#9650; = heading, one a minute</span>
          <span>U = turns back on itself</span>
        </div>

        {routes.some((r) => r.start) && (
          <div className="map-routes">
            <div className="map-count-title">Routes on the map ({routes.length})</div>
            {routes.map(({ item, color, number, arrows, uTurns, start }) => (
              <button
                type="button"
                key={`legend-${item.file}`}
                className="map-route-row"
                onClick={() => focusItem(item)}
                disabled={!start}
                title={start ? "Zoom to this video" : "No position for this video"}
              >
                <span
                  className="map-route-swatch"
                  style={{ background: start ? color : "transparent", borderColor: color }}
                >
                  {number}
                </span>
                <span className="map-route-name">
                  {item.name}
                  <small className="map-route-dates">{describeDates(item, mode)}</small>
                  <small>
                    {start
                      ? `${number}s to ${number}e - ${arrows.length} heading${arrows.length === 1 ? "" : "s"}`
                      : "no GPS, not on the map"}
                    {uTurns.length > 0
                      ? ` - ${uTurns.length} U-turn${uTurns.length === 1 ? "" : "s"} at ${uTurns
                          .map((t) => formatOffset(t.second))
                          .join(", ")}`
                      : ""}
                  </small>
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="map-issues">
          <div className="map-count-title">Videos needing attention ({issues.length})</div>
          {issues.length === 0 && status === "ready" && (
            <p className="status-text">Every video has GPS, readable metadata and a database record.</p>
          )}
          <ul>
            {issues.map((item) => (
              <li
                key={item.file}
                className={`map-issue ${item.status} ${item.dbStatus === "missing" ? "db-missing" : ""} ${item.position ? "" : "no-position"} ${focus?.key === item.file ? "focused" : ""}`}
                onClick={() => focusItem(item)}
                title={item.position ? "Show on map" : "No position could be estimated"}
              >
                <span className={`dot ${item.status}`} />
                <div>
                  <div className="map-issue-name">{item.name}</div>
                  <div className="map-issue-detail">{STATUS_LABEL[item.status]}{item.error ? `: ${item.error}` : ""}</div>
                  <div className={`map-issue-detail ${item.dbStatus === "missing" ? "map-issue-db-missing" : ""}`}>{describeDb(item)}</div>
                  <div className="map-issue-detail">{describeTime(item)}</div>
                  <div className="map-issue-detail">{describePosition(item)}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}
